import { format, parse } from 'date-fns';
import { StorePerformanceInterval } from '../constants';

export type ValueOptions = {
  locale?: string;
  options?: Intl.NumberFormatOptions;
};

export const formatValue = (value: number, { locale = 'default', options }: ValueOptions = {}): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
};

export const formatAmountWithSeparator = (
  value: number,
  unit?: string | null,
  decimals: number = 2,
  trimTrailingZeros: boolean = true,
): string => {
  const d = Math.max(0, Math.floor(decimals));

  const rounded = Number(value.toFixed(d));

  let formatted = rounded.toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

  // remove ".0" ONLY when trimTrailingZeros is true
  if (trimTrailingZeros) {
    formatted = formatted.replace(/\.0+$/, '');
  }

  return unit ? `${formatted}${unit}` : formatted;
};

export const formatAmountWithCurrency = (value: number, locale: string, currencyCode: string) => {
  const notation = value >= 10000 ? 'compact' : 'standard';

  return `${formatValue(value, {
    locale,
    options: { style: 'decimal', notation, maximumFractionDigits: 2 },
  })} ${currencyCode}`;
};

export const convertToCamelCase = (input: string) =>
  input.replace(/^([A-Z])|[\s-_](\w)/g, (_, a, b) => {
    if (b) return b.toUpperCase();
    return a.toLowerCase();
  });

export const convertCamelCaseToTitleCase = (input: string) => {
  // if the input role is "Other" show it as Store Advisor in UI
  if (input === 'other') {
    return 'Store Advisor';
  }
  return input
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space when an uppercase letter is followed by another uppercase and then a lowercase letter
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase letters (standard camelCase)
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Uppercase the first character of each word
    .join(' ');
};
export const removeDuplicateCharacters = (input: string) => [...new Set(input)].join('');

export const convertToKebabCase = (input: string, hasSpace: boolean = false) => {
  if (hasSpace) {
    return input
      .replace(/([a-z])([A-Z])/g, '$1-$2') // insert hyphen between lower and upper case
      .replace(/[\s_]+/g, '-') // replace spaces/underscores with hyphen
      .toLowerCase();
  }
  return input.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

export const getFormattedTimeUnit = (timeUnit: string, interval?: StorePerformanceInterval) => {
  if (interval === StorePerformanceInterval.WEEKLY) {
    const parsedDate = parse(timeUnit, 'yyyy-MM-dd', new Date());
    const isValidDate = !Number.isNaN(parsedDate.getTime());

    return isValidDate ? format(parsedDate, 'EEE') : timeUnit;
  } else {
    return timeUnit;
  }
};
